import { useState, useEffect } from 'react'
import { fetchDeletedProjects, restoreProject, purgeProject } from '../lib/api'
import { logChange } from '../lib/auditLog'

function fmtDate(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

export default function DeletedProjectsModal({ userProfile, onClose, onRestored }) {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [restoringId, setRestoringId] = useState(null)
  const [purgingId, setPurgingId] = useState(null)

  useEffect(() => {
    fetchDeletedProjects()
      .then(({ data }) => { setProjects(data || []); setLoading(false) })
  }, [])

  const purge = async (project) => {
    const label = project.aircraft?.registration || project.work_order || 'this project'
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone and will remove all subtasks and technician assignments.`)) return
    setPurgingId(project.id)
    try {
      await purgeProject(project.id)
      setProjects(prev => prev.filter(p => p.id !== project.id))
    } catch (err) {
      alert('Failed to permanently delete: ' + err.message)
    } finally {
      setPurgingId(null)
    }
  }

  const restore = async (project) => {
    setRestoringId(project.id)
    try {
      await restoreProject(project.id)
      await logChange({
        projectId: project.id,
        entityType: 'project',
        entityId: project.id,
        entityLabel: project.aircraft?.registration,
        action: 'restored',
        userProfile,
      })
      setProjects(prev => prev.filter(p => p.id !== project.id))
      onRestored?.()
    } finally {
      setRestoringId(null)
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Deleted Projects</h2>
              <p className="text-xs text-gray-400 mt-0.5">Restore a project to bring it and its subtasks back.</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
            ) : projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
                <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <p className="text-sm">No deleted projects</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['Aircraft', 'Work Order', 'Customer', 'Deleted'].map(h => (
                      <th key={h} className="px-5 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                    <th className="px-5 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {projects.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-semibold text-gray-800">
                        {p.aircraft?.registration || '—'}
                      </td>
                      <td className="px-5 py-3 text-gray-500">{p.work_order || '—'}</td>
                      <td className="px-5 py-3 text-gray-500">{p.customer_name || '—'}</td>
                      <td className="px-5 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(p.deleted_at)}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => restore(p)}
                            disabled={restoringId === p.id || purgingId === p.id}
                            className="text-xs font-medium text-brand-red hover:text-brand-red-dark border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50 disabled:opacity-40 transition-colors"
                          >
                            {restoringId === p.id ? 'Restoring…' : 'Restore'}
                          </button>
                          <button
                            onClick={() => purge(p)}
                            disabled={purgingId === p.id || restoringId === p.id}
                            className="text-xs font-medium text-red-600 hover:text-red-800 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50 disabled:opacity-40 transition-colors"
                          >
                            {purgingId === p.id ? 'Deleting…' : 'Delete Forever'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
